import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatCategory } from '../utils/formatCategory';
import { ARTICLE_CATEGORIES } from '../types';

interface CategoryCount {
  category: string;
  count: number;
}

interface CategoryListProps {
  categories: CategoryCount[];
  className?: string;
}

export function CategoryList({ categories, className = '' }: CategoryListProps) {
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 ${className}`}
    >
      <h2 className="text-xl font-semibold mb-4">Categories</h2>
      <motion.div className="space-y-2">
        <motion.div
          variants={itemVariants}
          className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
            !activeCategory 
              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
        >
          <Link 
            to="/blog"
            className="flex items-center gap-2 w-full"
          >
            <span>All Articles</span>
            <span className="ml-auto bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-sm">
              {categories.reduce((sum, cat) => sum + cat.count, 0)}
            </span>
          </Link>
        </motion.div>
        
        {categories.map(({ category, count }) => (
          <motion.div
            key={category}
            variants={itemVariants}
            className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
              activeCategory === category 
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <Link 
              to={`/blog?category=${category}`}
              className="flex items-center gap-2 w-full"
            >
              <span className="flex items-center gap-2">
                {ARTICLE_CATEGORIES[category as keyof typeof ARTICLE_CATEGORIES]} {formatCategory(category)}
              </span>
              <span className="ml-auto bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-sm">
                {count}
              </span>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}